import React, { createContext, PropsWithChildren, useContext } from "react";
import { useAppSelector } from "../../../hooks/reduxHooks";
import { generateClassName } from "../../../hooks/useAttributes";
import IElementProps from "../../../types/ElementProps";
import Logo from "../logo/Logo";
import "./Page.css";
import PageTitle from "./PageTitle";

type PageContext = {
  setTitle: (title: string) => void;
  setSubtitle: (subtitle: string) => void;
}

interface Props extends IElementProps {
  title?: string;
  subtitle?: string
  actions?: React.ReactNode | React.ReactNode[]
}


const PageContext = createContext<PageContext | null>(null);

export const usePage = () => {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error("usePage must be used within a Page component");
  }
  return context;
};

export default function Page(props: PropsWithChildren<Props>) {

  const [title, setTitle] = React.useState(props.title);
  const [subtitle, setSubtitle] = React.useState(props.subtitle);

  return (
    <PageContext.Provider value={{ setTitle, setSubtitle }}>
      <PageInner {...props} title={title} subtitle={subtitle} />
    </PageContext.Provider>
  )
}

function PageInner(props: PropsWithChildren<Props>) {

  const {
    title,
    subtitle,
    actions,
    children
  } = props;

  const {
    logoVisible
  } = useAppSelector(state => state.ui);

  return (
    <>
      <div className="w-100 d-flex flex-row align-items-center page-logo-container flex-wrap gap-2">
        <div className="d-flex flex-row align-items-center gap-3 page-logo">
          <div className={generateClassName("logo-container", {
            value: title || subtitle,
            onTrue: "d-sm-flex d-none",
            standard: "d-flex"
          })} >
            <Logo size={45} />
          </div>
          {
            (title || subtitle) && (
              <>
                <div className="page-header-divider d-sm-flex d-none" />
                <div className="d-flex flex-column">
                  <PageTitle title={title} subtitle={subtitle} />
                </div>
              </>
            )
          }
        </div>
        <div className="d-flex flex-row align-items-center ms-auto gap-2">
          {actions}
        </div>
      </div>
      <div className={generateClassName("d-flex flex-column page-content position-relative justify-content-start overflow-hidden", {
        value: logoVisible,
        standard: "page-content-no-logo"
      })} style={{ flexGrow: 1, minHeight: 0 }} >
        {children}
      </div>
    </>
  )
}